import React from "react";
import PropTypes from "prop-types";
import styles from "./FeedItem.module.css";
import classNames from "classnames";
import StandingOrderIcon from "../StandingOrderIcon";
import { extractColorValue } from "NewsAdmin/utils/extractColorValue";

/**
 * Interactivecomponent.
 * A button (feed item) that opens a feed modal on click.
 */
export function FeedItem({ feedItem, content }) {
  // Normalize colors to prevent default warnings
  const normalizeColor = (value, fallback = "#000000") => {
    if (!value || value === "default") return fallback;

    // Match "Label rgb(...)" or "Label #..."
    const match = value.match(/(rgb\(.+\))|(#.+)/);
    if (match) return match[1] || match[2];

    return fallback; // Anything else is invalid
  };

  let information = feedItem.information ? feedItem.information : undefined;

  // Update to use normalizeColor
  let headerTextColor = extractColorValue(information?.headerTextColor, "#FFFFFF");
  let subHeaderTextColor = extractColorValue(information?.subHeaderTextColor, "#FFFFFF");
  let headerBackgroundBackgroundColor = normalizeColor(
    information?.headerBackground?.backgroundColor,
    "transparent"
  );

  let maxHeight = information && information.maxHeight + "px";
  if (maxHeight && maxHeight == "px") {
    maxHeight = "300px";
  }

  // Cleanup logic and add additional safety checks for match
  if (information !== undefined) {
    // Extract header background color and convert it to rgba format and grab opacity
    // Added safety checks
    const backgroundColor = information?.headerBackground?.backgroundColor;
    const opacity = information?.headerBackground?.backgroundOpacity ?? 1;

    // Regular expression to match color values (hexadecimal or RGB) with prefix
    if (backgroundColor) {
      // Skip regex if there's no backgroundColor
      const colorMatch = backgroundColor.match(/(rgb\(.+\))|(#.+)/);
      if (colorMatch) {
        // Extract the color value from the matched group
        const colorValue = colorMatch[1] || colorMatch[2];

        if (colorValue.startsWith("#")) {
          // Parse hexadecimal color string
          const hexColor = colorValue.slice(1);
          const red = parseInt(hexColor.substring(0, 2), 16);
          const green = parseInt(hexColor.substring(2, 4), 16);
          const blue = parseInt(hexColor.substring(4, 6), 16);
          // Construct rgba color with opacity
          headerBackgroundBackgroundColor = `rgba(${red}, ${green}, ${blue}, ${opacity})`;
        } else if (colorValue.startsWith("rgb(")) {
          // Parse RGB color string
          const rgbMatch = colorValue.match(/\((\d+),\s*(\d+),\s*(\d+)\)/);
          if (rgbMatch) {
            const red = parseInt(rgbMatch[1]);
            const green = parseInt(rgbMatch[2]);
            const blue = parseInt(rgbMatch[3]);
            // Construct rgba color with opacity
            headerBackgroundBackgroundColor = `rgba(${red}, ${green}, ${blue}, ${opacity})`;
          }
        }
      }
    }
  }

  // Define and implement safety values
  const standingOrderAngle = feedItem?.data?.card?.gradient?.angle ?? 0;
  const standardContentAngle = information?.gradientAngle ?? 0;

  const standingOrderStops = (feedItem?.data?.card?.gradient?.stops || [])
    .map((stop) => stop.color || stop)
    .join(", ");

  const standardContentColors = (information?.gradientColors || [])
    .map((color) => {
      if (!color || typeof color !== "string") {
        return "";
      }

      const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgbMatch) {
        return `rgb(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]})`;
      }

      const hexMatch = color.match(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
      if (hexMatch) {
        return color;
      }

      const parts = color.split(" ");
      return parts.length > 1 ? parts[1] : color;
    })
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <div
        className={styles.container}
        style={{
          background: `linear-gradient(${
            feedItem.type === "Standing Order"
              ? standingOrderAngle + "deg"
              : standardContentAngle + "deg"
          }, ${
            feedItem.type === "Standing Order"
              ? standingOrderStops
              : standardContentColors
          })`,
        }}
      >
        <section className={styles.content}>
          {feedItem.type === "Standard Content" ? (
            <>
              <div className={styles.grid}>
                <div
                  className={styles["header-background"]}
                  style={{
                    backgroundColor: headerBackgroundBackgroundColor,
                  }}
                ></div>
                <div className={styles["header-container"]}>
                  <h1
                    className={styles.header}
                    style={{ color: headerTextColor }}
                  >
                    {information.header}
                  </h1>

                  {information.subHeader && (
                    <h2
                      className={styles["sub-header"]}
                      style={{ color: subHeaderTextColor }}
                    >
                      {information.subHeader}
                    </h2>
                  )}
                </div>
              </div>
              <div className={styles["img-container"]}>
                <div
                  className={classNames(styles["img-div"], {
                    [styles["no-padding"]]:
                      information.backgroundType === "Full Background",
                    [styles.left]: information.horizontalAlign === "left",
                    [styles.right]: information.horizontalAlign === "right",
                    [styles.center]: information.horizontalAlign === "center",
                    [styles.bottom]: !information.bottomPadding,
                  })}
                  style={
                    information.backgroundType === "Full Background"
                      ? {
                          backgroundImage: `url(${feedItem.imgSrc})`, // Update to use image normalization
                          backgroundPosition: `center`,
                          backgroundRepeat: "no-repeat",
                          backgroundSize: "cover",
                          height: "calc(100% + 55px)",
                        }
                      : {}
                  }
                >
                  <img
                    src={feedItem.imgSrc} // Update to use image normalization
                    alt="Feed Item"
                    style={{
                      width: maxHeight,
                      display:
                        information.backgroundType === "Full Background"
                          ? "none"
                          : "block",
                    }}
                  />
                </div>
              </div>
            </>
          ) : feedItem.type === "Standing Order" ? (
            <>
              <p
                className={styles["standing-order-preTitle"]}
                style={{ color: feedItem.data.card.header.topTitle.color }}
              >
                {feedItem.data.card.header.topTitle.text}
              </p>
              <div className={styles["standing-order-flex"]}>
                <div className={styles["standing-order-header"]}>
                  <h1
                    style={{
                      color: feedItem.data.card.header.title.color,
                    }}
                  >
                    {feedItem.data.card.header.title.text}
                  </h1>
                  <h2
                    style={{
                      color: feedItem.data.card.header.subTitle.color,
                    }}
                  >
                    {feedItem.data.card.header.subTitle.text}
                  </h2>
                </div>

                <div>
                  {/* Standing order Icon component wasn't even needed */}
                  <StandingOrderIcon
                    iconType={feedItem.data.card.image.path ?? undefined}
                    iconSize="120px"
                  />
                  <p
                    className={classNames(styles["icon-text"], {
                      [styles["icon-right"]]:
                        feedItem.data.card.image.alignment === "right",
                      [styles["icon-left"]]:
                        feedItem.data.card.image.alignment === "left",
                    })}
                    style={{
                      color: feedItem.data.card.image.subText.color,
                    }}
                  >
                    {feedItem.data.card.image.subText.text}
                  </p>
                </div>
              </div>
              <div className={styles["standing-order-details"]}>
                <p style={{ color: feedItem.data.card.details.sku.color }}>
                  {feedItem.data.card.details.sku.text}
                </p>

                <p style={{ color: feedItem.data.card.details.creator.color }}>
                  {feedItem.data.card.details.creator.text}
                </p>
                <p
                  style={{
                    color: feedItem.data.card.details.title.color,
                  }}
                >
                  {feedItem.data.card.details.title.text}
                </p>
              </div>
            </>
          ) : feedItem.type === "Banner" ? (
            <div className={styles.bannerWrapper}>
              <a
                href={feedItem.information?.link || "#"}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={feedItem.imgSrc}
                  alt={feedItem.options?.title || "Banner"}
                  className={styles.bannerImage}
                  style={{
                    objectPosition: feedItem.information?.position || "center",
                  }}
                />
              </a>
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
}

FeedItem.propTypes = {
  // feedItem: PropTypes.shape({
  //   type: PropTypes.oneOf(["Standard Content", "Standing Order"]),
  //   information: PropTypes.object,

  //   content: PropTypes.arrayOf(
  //     PropTypes.arrayOf(
  //       PropTypes.shape({
  //         type: PropTypes.oneOf([
  //           "text",
  //           "image",
  //           "divider",
  //           "media",
  //           "cms",
  //           "bsm",
  //         ]).isRequired,
  //       })
  //     )
  //   ),
  // }),

  // Cleanup PropTypes
  feedItem: PropTypes.shape({
    type: PropTypes.oneOf(["Standard Content", "Standing Order", "Banner"])
      .isRequired,
    information: PropTypes.object, // Optional for Standard Content only
    data: PropTypes.shape({
      // Optional for Standing Order only
      card: PropTypes.shape({
        gradient: PropTypes.shape({
          angle: PropTypes.number,
          stops: PropTypes.arrayOf(
            PropTypes.shape({
              color: PropTypes.string,
            })
          ),
        }),
        header: PropTypes.shape({
          topTitle: PropTypes.shape({
            text: PropTypes.string.isRequired,
            color: PropTypes.string,
          }),
          title: PropTypes.shape({
            text: PropTypes.string.isRequired,
            color: PropTypes.string,
          }),
          subTitle: PropTypes.shape({
            text: PropTypes.string.isRequired,
            color: PropTypes.string,
          }),
        }),
        image: PropTypes.shape({
          path: PropTypes.string.isRequired,
          alignment: PropTypes.string,
          subText: PropTypes.shape({
            text: PropTypes.string.isRequired,
            color: PropTypes.string,
          }),
        }),
        details: PropTypes.shape({
          sku: PropTypes.shape({
            text: PropTypes.string.isRequired,
            color: PropTypes.string,
          }),
          creator: PropTypes.shape({
            text: PropTypes.string.isRequired,
            color: PropTypes.string,
          }),
          title: PropTypes.shape({
            text: PropTypes.string.isRequired,
            color: PropTypes.string,
          }),
        }),
      }),
    }),
  }).isRequired,
};
